import React, { useEffect, useState } from 'react';
import { fetchInventoryLimitProducts, fetchInventoryLimits, fetchInventoryOverrides } from '../services/api';
import { EditDialog, Field, ListCard, PageTitle, WriteOffNote, useRows, useSave, useWriteLive } from '../components/StaffList';

/**
 * Tools & Lists > Inventory Limit: how many of a hard crab type can ship a day, with dated overrides.
 * These rows block days on the checkout calendar (Azure exit WS8). Reads sql01.Inventory and
 * InventoryOverrides. Saving is off until ws8_inventory_limit_live. Not CPx Catch.
 */

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const showDate = (ymd) => { const [y, m, d] = ymd.split('-'); return `${m}/${d}/${y}`; };
const emptyLimit = { inventoryId: 0, productId: '', defaultLimit: '', alwaysAvailable: '' };
const emptyOverride = { overrideId: 0, startDate: '', endDate: '', allowedDays: [], overrideLimit: 0 };

export default function InventoryLimit() {
  const live = useWriteLive('ws8_inventory_limit');
  const [f, setF] = useState({ alwaysAvailable: '', hasOverrides: '', defaultLimits: '0', defaultLimitsValue: '' });
  const [products, setProducts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [limit, setLimit] = useState(null);
  const [override, setOverride] = useState(null);
  const limits = useRows(() => fetchInventoryLimits(f), [f]);
  const overrides = useRows(() => (openId ? fetchInventoryOverrides(openId) : Promise.resolve([])), [openId]);
  const saveLimit = useSave(limits.reload);
  const saveOverride = useSave(() => { overrides.reload(); limits.reload(); });

  useEffect(() => { fetchInventoryLimitProducts().then(setProducts).catch(() => {}); }, []);

  const set = (key) => (e) => setF({ ...f, [key]: e.target.value });
  const confirmThen = (fn) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Do you want to delete?')) fn();
  };
  const submitLimit = async () => {
    const l = limit;
    if (!l.productId || l.defaultLimit === '' || Number(l.defaultLimit) < 0 || l.alwaysAvailable === '') return saveLimit.setError('Please enter all required fields.');
    if (await saveLimit.save('inventory_limit_save', { ...l, alwaysAvailable: l.alwaysAvailable === 'true' })) setLimit(null);
    return null;
  };
  const submitOverride = async () => {
    const o = override;
    if (!o.startDate || !o.endDate || o.overrideLimit === '') return saveOverride.setError('Please enter all required fields.');
    if (await saveOverride.save('inventory_override_save', { ...o, inventoryId: openId })) setOverride(null);
    return null;
  };
  const toggleDay = (d, on) => setOverride({ ...override, allowedDays: on ? [...override.allowedDays, d] : override.allowedDays.filter((x) => x !== d) });

  return (
    <div>
      <PageTitle>Inventory Limit</PageTitle>
      <WriteOffNote live={live} />
      <div className="port-filters">
        <label className="staff-check">Always Available
          <select className="form-select" value={f.alwaysAvailable} onChange={set('alwaysAvailable')}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select>
        </label>
        <label className="staff-check">Default Limits
          <select className="form-select" value={f.defaultLimits} onChange={set('defaultLimits')}><option value="0">All</option><option value="1">Equal</option><option value="2">Above</option><option value="3">Below</option></select>
        </label>
        <input type="number" className="form-input" style={{ width: 100 }} placeholder="Value" aria-label="Default limits value" value={f.defaultLimitsValue} onChange={set('defaultLimitsValue')} />
        <label className="staff-check">Has Overrides
          <select className="form-select" value={f.hasOverrides} onChange={set('hasOverrides')}><option value="">All</option><option value="true">Yes</option><option value="false">No</option></select>
        </label>
        <button type="button" className="btn btn-primary" disabled={!live} onClick={() => { saveLimit.setError(null); setLimit(emptyLimit); }}>Add Inventory Limit</button>
      </div>
      <ListCard title="Limits" rows={limits.rows} error={limits.error} empty="No limits.">
        <table className="trans-table">
          <thead><tr><th>Always Available</th><th>Name</th><th>Default Limits</th><th>Overrides</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {(limits.rows || []).map((r) => (
              <tr key={r.inventoryId}>
                <td>{r.alwaysAvailable ? 'Yes' : 'No'}</td>
                <td>{r.productId}</td>
                <td>{r.defaultQuantity}</td>
                <td>{r.overridesCount}</td>
                <td className="staff-actions">
                  <button type="button" className="btn btn-ghost" disabled={!live || saveLimit.saving} onClick={() => { saveLimit.setError(null); setLimit({ inventoryId: r.inventoryId, productId: r.productId, defaultLimit: r.defaultQuantity, alwaysAvailable: String(r.alwaysAvailable) }); }}>Edit</button>
                  <button type="button" className="btn btn-ghost" disabled={!live || saveLimit.saving} onClick={() => confirmThen(() => saveLimit.save('inventory_limit_delete', { id: r.inventoryId }))}>Delete</button>
                  <button type="button" className="btn btn-ghost" onClick={() => setOpenId(openId === r.inventoryId ? null : r.inventoryId)}>{openId === r.inventoryId ? 'Hide Overrides' : 'Overrides'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListCard>
      {saveLimit.error && !limit && <div className="staff-msg staff-msg-error">{saveLimit.error}</div>}
      {openId && (
        <div className="staff-section">
          <div className="port-filters">
            <button type="button" className="btn btn-primary" disabled={!live} onClick={() => { saveOverride.setError(null); setOverride(emptyOverride); }}>Add Inventory Override</button>
          </div>
          <ListCard title="Overrides" rows={overrides.rows} error={overrides.error} empty="No overrides.">
            <table className="trans-table">
              <thead><tr><th>Start Date</th><th>End Date</th><th>Allowed Days</th><th>Override Limit</th><th aria-label="Actions" /></tr></thead>
              <tbody>
                {(overrides.rows || []).map((o) => (
                  <tr key={o.overrideId}>
                    <td>{showDate(o.startDate)}</td>
                    <td>{showDate(o.endDate)}</td>
                    <td>{o.allowedDays}</td>
                    <td>{o.overrideQuantity}</td>
                    <td className="staff-actions">
                      <button type="button" className="btn btn-ghost" disabled={!live || saveOverride.saving} onClick={() => { saveOverride.setError(null); setOverride({ overrideId: o.overrideId, startDate: o.startDate, endDate: o.endDate, allowedDays: o.allowedDays ? o.allowedDays.split(',') : [], overrideLimit: o.overrideQuantity }); }}>Edit</button>
                      <button type="button" className="btn btn-ghost" disabled={!live || saveOverride.saving} onClick={() => confirmThen(() => saveOverride.save('inventory_override_delete', { id: o.overrideId }))}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ListCard>
          {saveOverride.error && !override && <div className="staff-msg staff-msg-error">{saveOverride.error}</div>}
        </div>
      )}
      {limit && (
        <EditDialog title={limit.inventoryId ? 'Edit Inventory Limit' : 'Add Inventory Limit'} live={live} saving={saveLimit.saving} error={saveLimit.error} onClose={() => setLimit(null)} onSave={submitLimit}>
          <Field label="Product">
            <select className="form-select" value={limit.productId} onChange={(e) => setLimit({ ...limit, productId: e.target.value })}>
              <option value="">Select a product</option>
              {limit.productId && !products.some((p) => p.name === limit.productId) && <option value={limit.productId}>{limit.productId}</option>}
              {products.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Default Limit"><input type="number" min="0" className="form-input" value={limit.defaultLimit} onChange={(e) => setLimit({ ...limit, defaultLimit: e.target.value })} /></Field>
          <Field label="Always Available">
            <select className="form-select" value={limit.alwaysAvailable} onChange={(e) => setLimit({ ...limit, alwaysAvailable: e.target.value })}>
              <option value="">Select</option><option value="true">Yes</option><option value="false">No</option>
            </select>
          </Field>
        </EditDialog>
      )}
      {override && (
        <EditDialog title={override.overrideId ? 'Edit Inventory Override' : 'Add Inventory Override'} live={live} saving={saveOverride.saving} error={saveOverride.error} onClose={() => setOverride(null)} onSave={submitOverride}>
          <Field label="Start Date"><input type="date" className="form-input" value={override.startDate} onChange={(e) => setOverride({ ...override, startDate: e.target.value })} /></Field>
          <Field label="End Date"><input type="date" className="form-input" value={override.endDate} onChange={(e) => setOverride({ ...override, endDate: e.target.value })} /></Field>
          <Field label="Allowed Days (leave empty for all days)">
            <div className="staff-grid">{DAYS.map((d) => <label key={d} className="staff-check"><input type="checkbox" checked={override.allowedDays.includes(d)} onChange={(e) => toggleDay(d, e.target.checked)} /> {d}</label>)}</div>
          </Field>
          <Field label="Override Limit"><input type="number" className="form-input" value={override.overrideLimit} onChange={(e) => setOverride({ ...override, overrideLimit: e.target.value })} /></Field>
        </EditDialog>
      )}
    </div>
  );
}
